// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * App Theme Context
 *
 * Manages application-wide color scheme/theme for Mantine components.
 * This controls dashboard colors, buttons, cards, and other UI elements
 * (excluding the header which is controlled by ThemeColorContext).
 *
 * Provides eight theme options:
 * - Default (original CarePoint theme with navy and teal)
 * - Serene Sage (earthy greens and neutrals)
 * - Olive Oasis (olive greens with terracotta accents)
 * - Pastel Paradise (soft lavender, blush pink, and muted sage)
 * - Spring Revival (fresh sage greens with cool grays)
 * - Modern Minty (warm cream, blush, sage green, and mauve)
 * - Sageberry Bliss (soft lavender purples with sage green)
 * - Rustic Retreat (earthy browns and olives with deep forest green)
 *
 * Preferences are persisted to localStorage (no PHI involved).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { JSX, ReactNode } from 'react';
import type { MantineColorsTuple, MantineTheme, MantineThemeOverride } from '@mantine/core';

const STORAGE_KEY = 'carepoint-app-theme';
const STYLE_STORAGE_KEY = 'carepoint-app-style';
const DARK_MODE_TINT_STORAGE_KEY = 'carepoint-dark-mode-tint';
const DARK_MODE_BASE_STORAGE_KEY = 'carepoint-dark-mode-base';

export type AppThemeId = 'default' | 'serene-sage' | 'olive-oasis' | 'pastel-paradise' | 'spring-revival' | 'modern-minty' | 'sageberry-bliss' | 'rustic-retreat' | 'editorial';
export type AppStyleId = 'default' | 'flat';
export type DarkModeTintId = 'enhanced' | 'medium' | 'gentle';
export type DarkModeBaseId = 'neutral' | 'blue';

export interface AppStyleDefinition {
  id: AppStyleId;
  name: string;
  description: string;
}

export interface DarkModeTintColors {
  // Background colors (from darkest to lightest)
  dark9: string; // Deepest background
  dark8: string; // Main background
  dark7: string; // Card/panel background
  dark6: string; // Elevated surfaces
  dark5: string; // Hover states
  dark4: string; // Borders
  // Text colors
  gray0: string; // Primary text
  gray3: string; // Secondary text
  gray5: string; // Muted text
}

export interface DarkModeTintDefinition {
  id: DarkModeTintId;
  name: string;
  description: string;
  // CSS variable values for this tint level - keyed by base type
  colors: {
    neutral: DarkModeTintColors;
    blue: DarkModeTintColors;
  };
}

export interface DarkModeBaseDefinition {
  id: DarkModeBaseId;
  name: string;
  description: string;
}

export const DARK_MODE_TINTS: DarkModeTintDefinition[] = [
  {
    id: 'enhanced',
    name: 'Enhanced',
    description: 'High contrast with deeper blacks',
    colors: {
      // Pure gray (neutral) - HSL(0, 0%, x%)
      neutral: {
        dark9: '#0a0a0a',  // HSL(0, 0%, 4%)
        dark8: '#121212',  // HSL(0, 0%, 7%)
        dark7: '#1a1a1a',  // HSL(0, 0%, 10%)
        dark6: '#242424',  // HSL(0, 0%, 14%)
        dark5: '#2e2e2e',  // HSL(0, 0%, 18%)
        dark4: '#3a3a3a',  // HSL(0, 0%, 23%)
        gray0: '#ffffff',
        gray3: '#e0e0e0',
        gray5: '#a0a0a0',
      },
      // Blue gray - HSL(210-212, 18-27%, x%)
      blue: {
        dark9: '#090b0e',  // HSL(210, 25%, 4%)
        dark8: '#10141a',  // HSL(212, 25%, 8%)
        dark7: '#171c24',  // HSL(211, 20%, 12%)
        dark6: '#1e2530',  // HSL(212, 23%, 15%)
        dark5: '#272f3d',  // HSL(211, 21%, 20%)
        dark4: '#3a4556',  // HSL(212, 20%, 28%)
        gray0: '#ffffff',
        gray3: '#d9e0e8',
        gray5: '#9aa8b8',
      },
    },
  },
  {
    id: 'medium',
    name: 'Medium',
    description: 'Balanced dark mode (default)',
    colors: {
      // Pure gray (neutral)
      neutral: {
        dark9: '#141414',  // HSL(0, 0%, 8%)
        dark8: '#1a1a1a',  // HSL(0, 0%, 10%)
        dark7: '#262626',  // HSL(0, 0%, 15%)
        dark6: '#2e2e2e',  // HSL(0, 0%, 18%)
        dark5: '#3b3b3b',  // HSL(0, 0%, 23%)
        dark4: '#525252',  // HSL(0, 0%, 32%)
        gray0: '#f8f9fa',
        gray3: '#dee2e6',
        gray5: '#adb5bd',
      },
      // Blue gray
      blue: {
        dark9: '#141517',  // Original medium values (already blue-ish)
        dark8: '#1a1b1e',
        dark7: '#25262b',
        dark6: '#2c2e33',
        dark5: '#373a40',
        dark4: '#4d4f56',
        gray0: '#f8f9fa',
        gray3: '#dee2e6',
        gray5: '#adb5bd',
      },
    },
  },
  {
    id: 'gentle',
    name: 'Gentle',
    description: 'Softer, easier on the eyes',
    colors: {
      // Pure gray (neutral)
      neutral: {
        dark9: '#1f1f1f',  // HSL(0, 0%, 12%)
        dark8: '#292929',  // HSL(0, 0%, 16%)
        dark7: '#333333',  // HSL(0, 0%, 20%)
        dark6: '#404040',  // HSL(0, 0%, 25%)
        dark5: '#4d4d4d',  // HSL(0, 0%, 30%)
        dark4: '#666666',  // HSL(0, 0%, 40%)
        gray0: '#f1f3f5',
        gray3: '#ced4da',
        gray5: '#909296',
      },
      // Blue gray
      blue: {
        dark9: '#1e2023',  // Original gentle values
        dark8: '#262a2e',
        dark7: '#2f3439',
        dark6: '#383e44',
        dark5: '#454c54',
        dark4: '#5c6470',
        gray0: '#f1f3f5',
        gray3: '#ced4da',
        gray5: '#909296',
      },
    },
  },
];

export const DARK_MODE_BASES: DarkModeBaseDefinition[] = [
  {
    id: 'neutral',
    name: 'Pure Gray',
    description: 'Clean neutral grays without color tint',
  },
  {
    id: 'blue',
    name: 'Blue Gray',
    description: 'Subtle blue tint for a softer look',
  },
];

export const APP_STYLES: AppStyleDefinition[] = [
  {
    id: 'default',
    name: 'Default',
    description: 'Standard look with background colors on panels and cards',
  },
  {
    id: 'flat',
    name: 'Flat',
    description: 'Clean, paperlike look with transparent backgrounds',
  },
];

/**
 * Optional non-color design overrides a theme may declare.
 *
 * Themes were originally color-only, with typography, radius and density fixed
 * in `baseTheme` inside DynamicMantineProvider and shared by every theme. A
 * theme that only swaps colors cannot express an editorial or high-density
 * design language, so these fields let a theme carry its whole design.
 *
 * Every field is optional. A theme that omits them inherits the shared
 * `baseTheme` exactly as before, which is why the original eight themes are
 * unaffected by this addition.
 */
export interface AppThemeDesign {
  /** Body font stack. */
  fontFamily?: string;
  /** Heading font stack — may differ from body (e.g. a serif display face). */
  headingFontFamily?: string;
  /** Heading weight, as a string because that is what Mantine expects. */
  headingFontWeight?: string;
  /** Monospace stack. Used for numerics where a theme wants tabular figures. */
  monoFontFamily?: string;
  /** Default radius applied to components that do not specify one. */
  defaultRadius?: string | number;
  /** Radius scale override. */
  radius?: Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', string>>;
  /** Type scale override. */
  fontSizes?: Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', string>>;
  /** Spacing scale override. */
  spacing?: Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', string>>;
  /** Which palette index filled components use. */
  primaryShade?: number;
  /** Per-component defaultProps / styles, merged over baseTheme's. */
  components?: MantineThemeOverride['components'];
  /**
   * Web fonts to load when — and only when — this theme is active. Themes using
   * system fonts declare nothing and load nothing, so adding a webfont-based
   * theme costs the others no bytes.
   */
  webFonts?: string[];
  /**
   * Body class applied while this theme is active, so CSS modules can opt into
   * theme-specific treatment without a global selector war.
   */
  bodyClass?: string;
}

export interface AppThemeDefinition {
  id: AppThemeId;
  name: string;
  description: string;
  /** Non-color design language. Omit to inherit the shared base theme. */
  design?: AppThemeDesign;
  colors: {
    primary: MantineColorsTuple;
    secondary: MantineColorsTuple;
    accent: MantineColorsTuple;
    neutral: MantineColorsTuple;
    success: MantineColorsTuple;
    warning: MantineColorsTuple;
    error: MantineColorsTuple;
  };
  primaryColorName: string;
  previewColors: string[]; // 5 colors for the preview swatch
}

// Default CarePoint theme (original colors from main.tsx)
const DEFAULT_THEME: AppThemeDefinition = {
  id: 'default',
  name: 'Default',
  description: 'Original CarePoint theme with navy and teal accents',
  colors: {
    primary: [
      '#e8eef4',
      '#d1dde9',
      '#a3bbcf',
      '#7599b5',
      '#4a789d',
      '#3d5a80', // Primary navy
      '#364f70',
      '#2f4460',
      '#283950',
      '#212e40',
    ],
    secondary: [
      '#e6f7f5',
      '#ccefeb',
      '#99dfd7',
      '#66cfc3',
      '#33bfaf',
      '#0fb5a5', // Primary teal
      '#0da394',
      '#0b9183',
      '#097f72',
      '#076d61',
    ],
    accent: [
      '#f3f0f7',
      '#e7e1ef',
      '#cfc3df',
      '#b7a5cf',
      '#9f87bf',
      '#8b6fb0', // Soft purple
      '#7d639e',
      '#6f578c',
      '#614b7a',
      '#533f68',
    ],
    neutral: [
      '#f8f9fa',
      '#f1f3f5',
      '#e9ecef',
      '#dee2e6',
      '#ced4da',
      '#adb5bd',
      '#6c757d',
      '#495057',
      '#343a40',
      '#212529',
    ],
    success: [
      '#e6f7ef',
      '#ccefdf',
      '#99dfbf',
      '#66cf9f',
      '#33bf7f',
      '#10b981',
      '#0ea573',
      '#0c9165',
      '#0a7d57',
      '#086949',
    ],
    warning: [
      '#fef7e6',
      '#fdefcc',
      '#fbdf99',
      '#f9cf66',
      '#f7bf33',
      '#f59e0b',
      '#dd8e0a',
      '#c47e09',
      '#ac6e08',
      '#945e07',
    ],
    error: [
      '#fef2f2',
      '#fee2e2',
      '#fecaca',
      '#fca5a5',
      '#f87171',
      '#ef4444',
      '#dc2626',
      '#b91c1c',
      '#991b1b',
      '#7f1d1d',
    ],
  },
  primaryColorName: 'navy',
  previewColors: ['#3d5a80', '#0fb5a5', '#8b6fb0', '#adb5bd', '#10b981'],
};

// Serene Sage theme - Calming sage greens with warm neutrals
// Based on palette: #F1EAD8, #D5C7AD, #8A8E75, #BEC5A4, #68604D
const SERENE_SAGE_THEME: AppThemeDefinition = {
  id: 'serene-sage',
  name: 'Serene Sage',
  description: 'Calming sage greens with warm cream and earth tones',
  colors: {
    primary: [
      '#f4f5f1', // Lightest sage
      '#e8eae2',
      '#d1d5c5',
      '#bac0a8',
      '#a3ab8b',
      '#8A8E75', // Primary sage green
      // Deepened from #7a7e67, which reached only 4.19:1 with white and 4.04:1
      // with dark text — the filled shade had no readable option either way.
      '#70745d',
      '#6a6e59',
      '#5a5e4b',
      '#4a4e3d',
    ],
    secondary: [
      '#f7f5f0',
      '#eeebe1',
      '#ddd7c6',
      '#ccc3ab',
      '#bbaf90',
      '#D5C7AD', // Warm tan
      '#c0b39b',
      '#ab9f89',
      '#968b77',
      '#817765',
    ],
    accent: [
      '#f8f6f2',
      '#f1ead8', // Cream (from palette)
      '#e5dcc4',
      '#d9ceb0',
      '#cdc09c',
      '#BEC5A4', // Light sage (from palette)
      '#aab193',
      '#969d82',
      '#828971',
      '#6e7560',
    ],
    neutral: [
      '#f5f4f2',
      '#ebeae6',
      '#d7d6d0',
      '#c3c2ba',
      '#afaea4',
      '#9b9a8e',
      '#7a7970',
      '#68604D', // Dark earth (from palette)
      '#4a453c',
      '#2c2a25',
    ],
    success: [
      '#eef5ec',
      '#ddebd9',
      '#bbd7b3',
      '#99c38d',
      '#77af67',
      '#5a9b4a',
      '#4e8740',
      '#427336',
      '#365f2c',
      '#2a4b22',
    ],
    warning: [
      '#fdf6e8',
      '#fbedd1',
      '#f7dba3',
      '#f3c975',
      '#efb747',
      '#d9a032',
      '#c18c2a',
      '#a97822',
      '#91641a',
      '#795012',
    ],
    error: [
      '#fdf2f1',
      '#fbe5e3',
      '#f7cbc7',
      '#f3b1ab',
      '#ef978f',
      '#c9726a',
      '#b0635c',
      '#97544e',
      '#7e4540',
      '#653632',
    ],
  },
  primaryColorName: 'sage',
  previewColors: ['#F1EAD8', '#D5C7AD', '#8A8E75', '#BEC5A4', '#68604D'],
};

// Olive Oasis theme - Olive greens with terracotta and slate accents
// Based on palette: #757F64, #CB7A5C, #E9E2D8, #C7CDBF, #5C757A
const OLIVE_OASIS_THEME: AppThemeDefinition = {
  id: 'olive-oasis',
  name: 'Olive Oasis',
  description: 'Rich olive greens with warm terracotta and cool slate accents',
  colors: {
    primary: [
      '#f2f3f0',
      '#e5e7e1',
      '#cbcfc3',
      '#b1b7a5',
      '#979f87',
      '#757F64', // Primary olive (from palette)
      '#677058',
      '#59614c',
      '#4b5240',
      '#3d4334',
    ],
    secondary: [
      '#fcf5f2',
      '#f9ebe5',
      '#f3d7cb',
      '#edc3b1',
      '#e7af97',
      '#CB7A5C', // Terracotta (from palette)
      '#b66d52',
      '#a16048',
      '#8c533e',
      '#774634',
    ],
    accent: [
      '#f1f2f3',
      '#e3e6e7',
      '#c7cdcf',
      '#abb4b7',
      '#8f9b9f',
      '#5C757A', // Slate blue-gray (from palette)
      '#51676b',
      '#46595c',
      '#3b4b4d',
      '#303d3e',
    ],
    neutral: [
      '#f8f7f5',
      '#f1efeb',
      '#E9E2D8', // Warm cream (from palette)
      '#d5cdc0',
      '#C7CDBF', // Light sage (from palette)
      '#a9b0a1',
      '#8a9183',
      '#6b7265',
      '#4c5347',
      '#2d3429',
    ],
    success: [
      '#eef5ec',
      '#ddebd9',
      '#bbd7b3',
      '#99c38d',
      '#77af67',
      '#5a9b4a',
      '#4e8740',
      '#427336',
      '#365f2c',
      '#2a4b22',
    ],
    warning: [
      '#fdf6e8',
      '#fbedd1',
      '#f7dba3',
      '#f3c975',
      '#efb747',
      '#d9a032',
      '#c18c2a',
      '#a97822',
      '#91641a',
      '#795012',
    ],
    error: [
      '#fdf2f1',
      '#fbe5e3',
      '#f7cbc7',
      '#f3b1ab',
      '#ef978f',
      '#c9726a',
      '#b0635c',
      '#97544e',
      '#7e4540',
      '#653632',
    ],
  },
  primaryColorName: 'olive',
  previewColors: ['#757F64', '#CB7A5C', '#E9E2D8', '#C7CDBF', '#5C757A'],
};

// Pastel Paradise theme - Soft lavender, blush, slate blue, and sage
// Based on palette: #C0B0C1, #E6C8C6, #859EAC, #97AC9F, #6E8175
const PASTEL_PARADISE_THEME: AppThemeDefinition = {
  id: 'pastel-paradise',
  name: 'Pastel Paradise',
  description: 'Soft lavender, blush pink, and muted sage tones',
  colors: {
    primary: [
      '#f5f2f5',
      '#ebe5eb',
      '#d7ccd8',
      '#c3b3c4',
      '#af9ab0',
      '#C0B0C1', // Primary lavender
      '#a99caa',
      '#928893',
      '#7b747c',
      '#646065',
    ],
    secondary: [
      '#fcf5f4',
      '#f9ebea',
      '#f3d7d5',
      '#edc3c0',
      '#e7afab',
      '#E6C8C6', // Blush pink
      '#cfb4b2',
      '#b8a09e',
      '#a18c8a',
      '#8a7876',
    ],
    accent: [
      '#f1f4f5',
      '#e3e9eb',
      '#c7d3d7',
      '#abbdc3',
      '#8fa7af',
      '#859EAC', // Slate blue
      '#758b98',
      '#657884',
      '#556570',
      '#45525c',
    ],
    neutral: [
      '#f5f6f5',
      '#ebecea',
      '#d7d9d5',
      '#c3c6c0',
      '#afb3ab',
      '#97AC9F', // Muted sage
      '#859a8d',
      '#73887b',
      '#617669',
      '#4f6457',
    ],
    success: [
      '#f0f5f2',
      '#e1ebe5',
      '#c3d7cb',
      '#a5c3b1',
      '#87af97',
      '#6E8175', // Dark sage (from palette)
      '#617367',
      '#546559',
      '#47574b',
      '#3a493d',
    ],
    warning: [
      '#fdf6e8',
      '#fbedd1',
      '#f7dba3',
      '#f3c975',
      '#efb747',
      '#d9a032',
      '#c18c2a',
      '#a97822',
      '#91641a',
      '#795012',
    ],
    error: [
      '#fdf2f2',
      '#fbe6e6',
      '#f7cdcd',
      '#f3b4b4',
      '#ef9b9b',
      '#c97272',
      '#b06464',
      '#975656',
      '#7e4848',
      '#653a3a',
    ],
  },
  primaryColorName: 'lavender',
  previewColors: ['#C0B0C1', '#E6C8C6', '#859EAC', '#97AC9F', '#6E8175'],
};

// Spring Revival theme - Fresh sage greens with cool gray and warm beige
// Based on palette: #939C81, #B8BD9F, #B3B8BC, #BAAF9D, #EBEBE1
const SPRING_REVIVAL_THEME: AppThemeDefinition = {
  id: 'spring-revival',
  name: 'Spring Revival',
  description: 'Fresh sage greens with cool grays and warm beige',
  colors: {
    primary: [
      '#f3f4f1',
      '#e7e9e3',
      '#cfd3c7',
      '#b7bdab',
      '#9fa78f',
      '#939C81', // Primary sage
      '#828b72',
      '#717a63',
      '#606954',
      '#4f5845',
    ],
    secondary: [
      '#f6f7f3',
      '#edefe7',
      '#dbdfcf',
      '#c9cfb7',
      '#b7bf9f',
      '#B8BD9F', // Light sage
      '#a5aa8f',
      '#92977f',
      '#7f846f',
      '#6c715f',
    ],
    accent: [
      '#f5f5f6',
      '#ebeced',
      '#d7d9db',
      '#c3c6c9',
      '#afb3b7',
      '#B3B8BC', // Cool gray
      '#9fa4a8',
      '#8b9094',
      '#777c80',
      '#63686c',
    ],
    neutral: [
      '#f7f6f4',
      '#efede8',
      '#dfdbd1',
      '#cfc9ba',
      '#bfb7a3',
      '#BAAF9D', // Warm beige
      '#a79c8c',
      '#94897b',
      '#81766a',
      '#6e6359',
    ],
    success: [
      '#f2f5f0',
      '#e5ebe1',
      '#cbd7c3',
      '#b1c3a5',
      '#97af87',
      '#7d9b6a',
      '#6e885d',
      '#5f7550',
      '#506243',
      '#414f36',
    ],
    warning: [
      '#fdf6e8',
      '#fbedd1',
      '#f7dba3',
      '#f3c975',
      '#efb747',
      '#d9a032',
      '#c18c2a',
      '#a97822',
      '#91641a',
      '#795012',
    ],
    error: [
      '#fdf2f1',
      '#fbe5e3',
      '#f7cbc7',
      '#f3b1ab',
      '#ef978f',
      '#c9726a',
      '#b0635c',
      '#97544e',
      '#7e4540',
      '#653632',
    ],
  },
  primaryColorName: 'spring',
  previewColors: ['#939C81', '#B8BD9F', '#B3B8BC', '#BAAF9D', '#EBEBE1'],
};

// Modern Minty theme - Warm cream, blush, sage green, slate, and mauve
// Based on palette: #ECE4D9, #E8D1C3, #B7BEA0, #ABB5B6, #B18B7E
const MODERN_MINTY_THEME: AppThemeDefinition = {
  id: 'modern-minty',
  name: 'Modern Minty',
  description: 'Warm cream and blush with sage green and earthy mauve',
  colors: {
    primary: [
      '#f3f4f0',
      '#e7e9e1',
      '#cfd3c3',
      '#b7bda5',
      '#9fa787',
      '#B7BEA0', // Primary sage green
      '#a3aa8f',
      '#8f967e',
      '#7b826d',
      '#676e5c',
    ],
    secondary: [
      '#fbf8f6',
      '#f7f1ed',
      '#efe3db',
      '#e7d5c9',
      '#dfc7b7',
      '#E8D1C3', // Warm blush
      '#d0bcaf',
      '#b8a79b',
      '#a09287',
      '#887d73',
    ],
    accent: [
      '#f6f3f2',
      '#ede7e5',
      '#dbcfcb',
      '#c9b7b1',
      '#b79f97',
      '#B18B7E', // Earthy mauve
      '#9e7c70',
      '#8b6d62',
      '#785e54',
      '#654f46',
    ],
    neutral: [
      '#f5f5f6',
      '#ebeced',
      '#d7d9db',
      '#c3c6c9',
      '#afb3b7',
      '#ABB5B6', // Slate gray
      '#979fa0',
      '#83898a',
      '#6f7374',
      '#5b5d5e',
    ],
    success: [
      '#f0f5ec',
      '#e1ebd9',
      '#c3d7b3',
      '#a5c38d',
      '#87af67',
      '#6a9b4a',
      '#5c8740',
      '#4e7336',
      '#405f2c',
      '#324b22',
    ],
    warning: [
      '#fdf6e8',
      '#fbedd1',
      '#f7dba3',
      '#f3c975',
      '#efb747',
      '#d9a032',
      '#c18c2a',
      '#a97822',
      '#91641a',
      '#795012',
    ],
    error: [
      '#fdf2f1',
      '#fbe5e3',
      '#f7cbc7',
      '#f3b1ab',
      '#ef978f',
      '#c9726a',
      '#b0635c',
      '#97544e',
      '#7e4540',
      '#653632',
    ],
  },
  primaryColorName: 'minty',
  previewColors: ['#ECE4D9', '#E8D1C3', '#B7BEA0', '#ABB5B6', '#B18B7E'],
};

// Sageberry Bliss theme - Soft lavender purples with sage green accents
// Based on palette: #B296CA, #CEB7D7, #E0DCD1, #ACB9AD, #81987F
const SAGEBERRY_BLISS_THEME: AppThemeDefinition = {
  id: 'sageberry-bliss',
  name: 'Sageberry Bliss',
  description: 'Soft lavender purples with sage green and warm cream tones',
  colors: {
    primary: [
      '#f5f1f8',
      '#ebe3f1',
      '#d7c7e3',
      '#c3abd5',
      '#af8fc7',
      '#B296CA', // Primary lavender purple — the theme's identity, unchanged
      /*
       * Shades 6-9 deepened. Shade 6 is what Mantine fills buttons and badges
       * with, and the previous value (#9f85b5) reached only 3.23:1 against
       * white and 4.13:1 against dark text — the one theme where neither text
       * color cleared AA, so autoContrast alone could not rescue it. The hue
       * is preserved; the ramp is simply darker where it needs to carry text.
       */
      '#7a6291',
      '#6b5480',
      '#5c4470',
      '#4d3960',
    ],
    secondary: [
      '#f8f5fa',
      '#f1ebf5',
      '#e3d7eb',
      '#d5c3e1',
      '#c7afd7',
      '#CEB7D7', // Light lavender
      '#b9a4c2',
      '#a491ad',
      '#8f7e98',
      '#7a6b83',
    ],
    accent: [
      '#f5f6f5',
      '#ebecea',
      '#d7d9d5',
      '#c3c6c0',
      '#afb3ab',
      '#ACB9AD', // Sage green
      '#99a69a',
      '#869387',
      '#738074',
      '#606d61',
    ],
    neutral: [
      '#f8f7f6',
      '#f1efec',
      '#E0DCD1', // Warm cream (from palette)
      '#cdc7ba',
      '#bab2a3',
      '#a79d8c',
      '#8a8175',
      '#6d655e',
      '#504947',
      '#332d30',
    ],
    success: [
      '#f0f5f1',
      '#e1ebe3',
      '#c3d7c7',
      '#a5c3ab',
      '#87af8f',
      '#81987F', // Dark sage (from palette)
      '#728771',
      '#637663',
      '#546555',
      '#455447',
    ],
    warning: [
      '#fdf6e8',
      '#fbedd1',
      '#f7dba3',
      '#f3c975',
      '#efb747',
      '#d9a032',
      '#c18c2a',
      '#a97822',
      '#91641a',
      '#795012',
    ],
    error: [
      '#fdf2f2',
      '#fbe6e6',
      '#f7cdcd',
      '#f3b4b4',
      '#ef9b9b',
      '#c97272',
      '#b06464',
      '#975656',
      '#7e4848',
      '#653a3a',
    ],
  },
  primaryColorName: 'sageberry',
  previewColors: ['#B296CA', '#CEB7D7', '#E0DCD1', '#ACB9AD', '#81987F'],
};

// Rustic Retreat theme - Earthy browns and olives with deep forest green
// Based on palette: #96784E, #E6E3DE, #838264, #B5C1B8, #112917
const RUSTIC_RETREAT_THEME: AppThemeDefinition = {
  id: 'rustic-retreat',
  name: 'Rustic Retreat',
  description: 'Earthy browns and olive with deep forest green accents',
  colors: {
    primary: [
      '#f4f2ee',
      '#e9e5dd',
      '#d3cbbb',
      '#bdb199',
      '#a79777',
      '#96784E', // Primary brown/tan
      '#856b45',
      '#745e3c',
      '#635133',
      '#52442a',
    ],
    secondary: [
      '#f3f3f1',
      '#e7e7e3',
      '#cfcfc7',
      '#b7b7ab',
      '#9f9f8f',
      '#838264', // Olive (from palette)
      '#747359',
      '#65644e',
      '#565543',
      '#474638',
    ],
    accent: [
      '#e8ebe9',
      '#d1d7d3',
      '#bac3bd',
      '#a3afa7',
      '#8c9b91',
      '#B5C1B8', // Sage/silver (from palette)
      '#a2aea5',
      '#8f9b92',
      '#7c887f',
      '#69756c',
    ],
    neutral: [
      '#f9f9f8',
      '#f3f2f0',
      '#E6E3DE', // Warm off-white (from palette)
      '#d4d0c9',
      '#c2bdb4',
      '#afaa9f',
      '#8c877d',
      '#69645b',
      '#464139',
      '#231e17',
    ],
    success: [
      '#e6ebe7',
      '#cdd7cf',
      '#b4c3b7',
      '#9baf9f',
      '#829b87',
      '#112917', // Deep forest green (from palette)
      '#0f2414',
      '#0d1f11',
      '#0b1a0e',
      '#09150b',
    ],
    warning: [
      '#fdf6e8',
      '#fbedd1',
      '#f7dba3',
      '#f3c975',
      '#efb747',
      '#d9a032',
      '#c18c2a',
      '#a97822',
      '#91641a',
      '#795012',
    ],
    error: [
      '#fdf2f1',
      '#fbe5e3',
      '#f7cbc7',
      '#f3b1ab',
      '#ef978f',
      '#c9726a',
      '#b0635c',
      '#97544e',
      '#7e4540',
      '#653632',
    ],
  },
  primaryColorName: 'rustic',
  previewColors: ['#96784E', '#E6E3DE', '#838264', '#B5C1B8', '#112917'],
};

// Editorial theme — the NULOU HEALTH design language.
// Warm paper, near-black ink, zero radius, a serif display face with italic
// emphasis, and monospace numerics. Unlike the other themes this one declares a
// `design` block, because the language is mostly typography and density rather
// than color.
const EDITORIAL_THEME: AppThemeDefinition = {
  id: 'editorial',
  name: 'Editorial',
  description: 'Warm paper, ink serif display and monospace numerics. Zero radius, high density.',
  design: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    headingFontFamily: "'Instrument Serif', 'Iowan Old Style', Georgia, serif",
    headingFontWeight: '400',
    monoFontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
    defaultRadius: 0,
    radius: { xs: '0px', sm: '0px', md: '0px', lg: '0px', xl: '0px' },
    fontSizes: {
      xs: '0.625rem',
      sm: '0.6875rem',
      md: '0.8125rem',
      lg: '0.9375rem',
      xl: '1.125rem',
    },
    spacing: { xs: '4px', sm: '8px', md: '12px', lg: '18px', xl: '26px' },
    primaryShade: 6,
    webFonts: [
      'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap',
    ],
    bodyClass: 'app-theme-editorial',
    components: {
      Paper: { defaultProps: { radius: 0, shadow: undefined, withBorder: true } },
      Card: { defaultProps: { radius: 0, shadow: undefined, withBorder: true } },
      Button: { defaultProps: { radius: 0 }, styles: { root: { letterSpacing: '0.01em', fontWeight: 400 } } },
      ActionIcon: { defaultProps: { radius: 0 } },
      TextInput: { defaultProps: { radius: 0 } },
      Select: { defaultProps: { radius: 0 } },
      Textarea: { defaultProps: { radius: 0 } },
      Modal: { defaultProps: { radius: 0 } },
      Badge: {
        defaultProps: { radius: 0, variant: 'outline' },
        styles: {
          root: {
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 400,
          },
        },
      },
      Table: {
        styles: {
          th: {
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            fontWeight: 600,
          },
        },
      },
      Tabs: { defaultProps: { radius: 0 } },
    },
  },
  colors: {
    primary: [
      '#ecebe7',
      '#d9d9d7',
      '#b4b6b8',
      '#848a90',
      '#5a626c',
      '#353e4d',
      '#0f1b2d',
      '#0c1523',
      '#09101b',
      '#060b13',
    ],
    secondary: [
      '#edefe4',
      '#dae2ce',
      '#b7caa5',
      '#94b27d',
      '#749c58',
      '#5a8a3a',
      '#507b36',
      '#476d32',
      '#3c5c2d',
      '#304b28',
    ],
    accent: [
      '#f4eae4',
      '#ecd5cc',
      '#dcafa2',
      '#cc8977',
      '#be6651',
      '#b24a32',
      '#9e432f',
      '#8a3c2c',
      '#723428',
      '#5b2c24',
    ],
    neutral: [
      '#faf8f3',
      '#f4f1e9',
      '#eae5d9',
      '#e5e0d4',
      '#d4cdbc',
      '#a8a294',
      '#6b6659',
      '#4a4740',
      '#2a2822',
      '#14130f',
    ],
    success: [
      '#edefe4',
      '#dae2ce',
      '#b7caa5',
      '#94b27d',
      '#749c58',
      '#5a8a3a',
      '#507b36',
      '#476d32',
      '#3c5c2d',
      '#304b28',
    ],
    warning: [
      '#f4eee2',
      '#ece0c9',
      '#ddc59b',
      '#cdaa6d',
      '#bf9243',
      '#b47e22',
      '#a07121',
      '#8b641f',
      '#73541e',
      '#5c451c',
    ],
    error: [
      '#f6e8e3',
      '#f0d1cb',
      '#e5a69e',
      '#db7b72',
      '#d15349',
      '#c93429',
      '#b23027',
      '#9b2b25',
      '#802622',
      '#662120',
    ],
  },
  primaryColorName: 'ink',
  previewColors: ['#0F1B2D', '#5A8A3A', '#B47E22', '#B24A32', '#E5E0D4'],
};

export const APP_THEMES: AppThemeDefinition[] = [
  DEFAULT_THEME,
  SERENE_SAGE_THEME,
  OLIVE_OASIS_THEME,
  PASTEL_PARADISE_THEME,
  SPRING_REVIVAL_THEME,
  MODERN_MINTY_THEME,
  SAGEBERRY_BLISS_THEME,
  RUSTIC_RETREAT_THEME,
  EDITORIAL_THEME,
];

/**
 * Nudge a ramp's fill shades until a label can actually be read on them.
 *
 * A filled surface can only carry black or white. Across the nine themes, 34 of
 * the shades Mantine fills with (6, 7 and 8 of every ramp — primary, accent,
 * neutral, success, warning, error) landed in the 4.1-4.6 band, where *neither*
 * clears AA. They are mid-toned by design: that is what makes these palettes
 * look the way they do, and it is also what makes them unreadable.
 *
 * Always darker, never lighter. Moving the shorter distance looks better and is
 * wrong: these same shades are read as *text* on light surfaces — panel titles,
 * dimmed labels, list subtitles all resolve to a 6-8 shade — so lightening one
 * to let black sit on it as a fill takes the far more common text case below AA
 * at the same time. A first attempt did that for 17 of the 38 shades and turned
 * 4 failing theme/scheme combinations into 18. Darkening helps both readings.
 * @param palette - The ten-shade tuple as authored.
 * @returns The tuple with fill shades corrected where they had to be.
 */
function ensureLabelReadable(palette: MantineColorsTuple): MantineColorsTuple {
  const corrected = [...palette] as string[];
  for (const shade of FILL_SHADES) {
    const fill = corrected[shade];
    if (!fill || labelContrast(fill) >= AA_WITH_MARGIN) {
      continue;
    }
    corrected[shade] = darkenUntilReadable(fill);
  }
  return corrected as unknown as MantineColorsTuple;
}

/** The shades Mantine uses as a fill: primaryShade light, dark, and hover. */
const FILL_SHADES = [6, 7, 8];
const AA_WITH_MARGIN = 4.6;

/** Best ratio a black-or-white label can reach on `fill`. */
function labelContrast(fill: string): number {
  return Math.max(contrastRatio('#ffffff', fill), contrastRatio('#1a1d20', fill));
}

/**
 * Step `hex` toward black until white clears AA on it, in 2% increments.
 *
 * Interpolating toward black rather than editing channels individually, so the
 * hue is carried along unchanged — the shade gets deeper, not grayer.
 */
function darkenUntilReadable(hex: string): string {
  for (let step = 1; step <= 50; step += 1) {
    const candidate = blend('#000000', step * 0.02, hex);
    if (contrastRatio('#ffffff', candidate) >= AA_WITH_MARGIN) {
      return candidate;
    }
  }
  return '#1a1d20';
}

/**
 * `variant="light"` text color for a palette, per color scheme.
 *
 * Mantine derives this from shade 6, over a background that is the same color
 * at 10%, which lands around 3.2:1 — and the literals in index.css only cover
 * Mantine's stock palette names. Each theme registers its own primary under a
 * name of its choosing (`sage`, `olive`, `ink`…) and replaces gray, green, red,
 * teal and violet with its own ramps, so those had no checked value at all.
 * Serene Sage's came out at 4.10:1 and its error ramp at 4.39:1.
 *
 * Computed from the palette actually in play, so a new theme is covered without
 * anyone remembering to add it.
 * @param palette - The ten-shade tuple.
 * @returns Label colors keyed by scheme.
 */
export function lightVariantTextColors(palette: MantineColorsTuple): { light: string; dark: string } {
  return {
    // Darkest-last in light mode, lightest-last in dark: take the first that
    // clears AA so the hue stays as close to the palette as possible.
    light: pickAccessibleShade(palette, lightVariantSurface(palette, 'light'), [7, 8, 9], '#1a1d20'),
    dark: pickAccessibleShade(palette, lightVariantSurface(palette, 'dark'), [2, 1, 0], '#f1f3f5'),
  };
}

/**
 * The background Mantine paints behind a `variant="light"` label.
 *
 * Read out of Mantine's own variable generator rather than guessed: `-light` is
 * `alpha(colors[c][primaryShade], 0.12)`, and primaryShade defaults to 6 in
 * light and 8 in dark. An earlier version assumed 0.1 and shade 6 in both,
 * which put the computed background two points per channel lighter than the
 * rendered one — enough for a label to come out at 4.47:1 and fail a check that
 * had just passed in Node.
 * @param palette - The ten-shade tuple.
 * @param scheme - Color scheme.
 * @returns The composited background, as a hex string.
 */
function lightVariantSurface(palette: MantineColorsTuple, scheme: 'light' | 'dark'): string {
  return scheme === 'light'
    ? blend(palette[6], 0.12, '#ffffff')
    : blend(palette[8] ?? palette[6], 0.12, '#242424');
}

/**
 * `c="<name>"` text color for a palette, per color scheme.
 *
 * Mantine points `--mantine-color-<name>-text` at the filled color — shade 6 —
 * which is chosen to carry white *on* it, not to be read *on* the page. Serene
 * Sage's error ramp measured 4.39:1 as body text on white. Picked against the
 * page background rather than a tint, so it differs from
 * {@link lightVariantTextColors}.
 * @param palette - The ten-shade tuple.
 * @returns Text colors keyed by scheme.
 */
export function bodyTextColors(palette: MantineColorsTuple): { light: string; dark: string } {
  return {
    light: pickAccessibleShade(palette, '#ffffff', [6, 7, 8, 9], '#1a1d20'),
    dark: pickAccessibleShade(palette, '#242424', [4, 3, 2, 1], '#f1f3f5'),
  };
}

/**
 * First shade in `order` clearing AA against `background`, else `fallback`.
 *
 * Held to 4.6 rather than 4.5. The background is reconstructed by compositing,
 * and the browser's own rounding put it within two points per channel of the
 * computed value — so a shade that lands exactly on 4.5 here can measure 4.47
 * on screen. The margin costs one shade of saturation at worst.
 */
function pickAccessibleShade(
  palette: MantineColorsTuple,
  background: string,
  order: number[],
  fallback: string
): string {
  for (const shade of order) {
    const candidate = palette[shade];
    if (candidate && contrastRatio(candidate, background) >= AA_WITH_MARGIN) {
      return candidate;
    }
  }
  return fallback;
}

/** Composite `hex` at `alpha` over `base`, as Mantine's -light background is. */
function blend(hex: string, alpha: number, base: string): string {
  const channels = (value: string): number[] => {
    const h = value.replace('#', '');
    return [0, 2, 4].map((i) => Number.parseInt(h.slice(i, i + 2), 16));
  };
  const [r, g, b] = channels(hex);
  const [br, bg, bb] = channels(base);
  const mix = (f: number, back: number): string =>
    Math.round(f * alpha + back * (1 - alpha))
      .toString(16)
      .padStart(2, '0');
  return `#${mix(r, br)}${mix(g, bg)}${mix(b, bb)}`;
}

function relativeLuminance(hex: string): number {
  const h = hex.replace('#', '');
  const channel = (i: number): number => {
    const v = Number.parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** Near-black used for text on light fills; matches --text-strong (light). */
const DARK_TEXT = '#1a1d20';
/** Relative luminance of DARK_TEXT, precomputed so the comparison is exact. */
const DARK_TEXT_LUMINANCE = 0.0120262623566028;

/**
 * Text color that reads on a given fill.
 *
 * The same rule Mantine's `autoContrast` applies, exposed for the handful of
 * places that paint a theme color through a CSS custom property rather than a
 * Mantine component — those get no autoContrast, and hardcoded `color: white`
 * on a pastel primary is exactly the unreadable case.
 * @param hex - The fill color.
 * @returns White or near-black, whichever reaches the higher contrast.
 */
export function contrastTextOn(hex: string): string {
  const h = hex.replace('#', '');
  const channel = (i: number): number => {
    const v = Number.parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  /*
   * Compare the two candidates rather than thresholding the luminance.
   *
   * A threshold is an approximation of this comparison and it gets the
   * borderline cases wrong: Pastel Paradise's primary sits at luminance 0.258,
   * which a 0.3 threshold reads as "dark enough for white text" when white
   * actually measures 3.41 against it and dark text measures 4.97.
   */
  const ratio = (candidate: number): number => {
    const [hi, lo] = [luminance, candidate].sort((a, b) => b - a);
    return (hi + 0.05) / (lo + 0.05);
  };
  // Relative luminance of #ffffff and of DARK_TEXT.
  return ratio(1) >= ratio(DARK_TEXT_LUMINANCE) ? '#ffffff' : DARK_TEXT;
}

/**
 * Filled-button label colors for a palette, one per color scheme.
 *
 * Mantine's `autoContrast` does not work here. Verified in the running app: with
 * `autoContrast: true` and a `luminanceThreshold` set, every
 * `--mantine-color-<c>-contrast` variable is empty and
 * `--mantine-primary-color-contrast` is the hardcoded `#fff`, so filled buttons
 * get white text regardless of their fill. On the muted palettes that is white
 * on a pale fill: Serene Sage's secondary resolves to #c0b39b, which white
 * misses by a wide margin.
 *
 * So the value is computed here instead of asked for. Mantine's filled shade is
 * 6 in light mode and 8 in dark unless the theme overrides `primaryShade`.
 * @param palette - The ten-shade color tuple.
 * @param shadeOverride - The theme's primaryShade, if it sets one.
 * @returns Label colors keyed by scheme.
 */
function filledLabelColors(
  palette: MantineColorsTuple,
  shadeOverride?: number
): { light: string; dark: string } {
  const lightShade = shadeOverride ?? 6;
  const darkShade = shadeOverride ?? 8;
  return {
    light: contrastTextOn(palette[lightShade] ?? palette[6]),
    dark: contrastTextOn(palette[darkShade] ?? palette[8] ?? palette[6]),
  };
}

// Generate Mantine theme override from app theme definition
export function generateMantineTheme(theme: AppThemeDefinition): MantineThemeOverride {
  const d = theme.design;

  return {
    primaryColor: theme.primaryColorName,

    /*
     * Pick the text color on filled surfaces from the fill's luminance.
     *
     * Without this, filled buttons and badges use white text against whatever
     * the theme's primary shade happens to be. Measured across the nine themes,
     * white on the primary fill fell below WCAG AA on three of them — Pastel
     * Paradise (3.41), Modern Minty (3.07) and Sageberry Bliss (4.10) — because
     * their signature colors are mid-toned pastels. Dark text clears AA on the
     * first two, so letting Mantine choose fixes them without touching the
     * palettes. Sageberry needed a deeper ramp as well; see its primary tuple.
     */
    autoContrast: true,
    /*
     * Mantine picks dark text above this luminance. Its default is 0.3, which
     * chooses white for Spring Revival's, Pastel Paradise's and Modern Minty's
     * fills where dark text measures better. Across all nine palettes the
     * crossover between "white reads better" and "dark reads better" falls
     * between 0.2047 and 0.2128, so 0.21 makes Mantine's choice agree with the
     * measured one for every shade of every theme.
     *
     * Corrected: that window was computed against the *primary* palettes only,
     * and against #1a1d20 rather than the pure black Mantine actually uses.
     * Recomputed across every palette each theme defines, and against #000, the
     * window is (0.1787, 0.1816). 0.18 sits inside it.
     */
    luminanceThreshold: 0.18,

    colors: {
      [theme.primaryColorName]: ensureLabelReadable(theme.colors.primary),
      teal: ensureLabelReadable(theme.colors.secondary),
      violet: ensureLabelReadable(theme.colors.accent),
      gray: ensureLabelReadable(theme.colors.neutral),
      green: ensureLabelReadable(theme.colors.success),
      amber: ensureLabelReadable(theme.colors.warning),
      red: ensureLabelReadable(theme.colors.error),
      // Keep navy for backwards compatibility in default theme
      ...(theme.id === 'default' ? { navy: ensureLabelReadable(theme.colors.primary) } : {}),
    },

    // Non-color design language. Each key is emitted only when the theme
    // declares it, so `mergeThemeOverrides` leaves baseTheme's value in place
    // for every theme that does not.
    ...(d?.fontFamily ? { fontFamily: d.fontFamily } : {}),
    ...(d?.monoFontFamily ? { fontFamilyMonospace: d.monoFontFamily } : {}),
    ...(d?.headingFontFamily || d?.headingFontWeight
      ? {
          headings: {
            ...(d.headingFontFamily ? { fontFamily: d.headingFontFamily } : {}),
            ...(d.headingFontWeight ? { fontWeight: d.headingFontWeight } : {}),
          },
        }
      : {}),
    ...(d?.defaultRadius !== undefined ? { defaultRadius: d.defaultRadius } : {}),
    ...(d?.radius ? { radius: d.radius } : {}),
    ...(d?.fontSizes ? { fontSizes: d.fontSizes } : {}),
    ...(d?.spacing ? { spacing: d.spacing } : {}),
    ...(d?.primaryShade !== undefined ? { primaryShade: d.primaryShade } : {}),
    /*
     * Button label color, computed from the fill rather than left to
     * autoContrast, which emits nothing in this setup. Two custom properties
     * instead of one `light-dark()` value, because `light-dark()` inside a
     * custom property resolves to its light branch in both schemes — the bug
     * that made the whole token set light-only until it was found in a browser.
     * index.css picks between them per scheme.
     */
    components: {
      ...(d?.components ?? {}),
      Button: {
        ...(d?.components?.Button ?? {}),
        vars: (mantineTheme: MantineTheme, props: { variant?: string; color?: string }) => {
          const variant = props.variant ?? 'filled';
          if (variant !== 'filled') {
            return { root: {} };
          }
          const key = props.color ?? mantineTheme.primaryColor;
          const palette = mantineTheme.colors[key as keyof typeof mantineTheme.colors];
          if (!palette) {
            return { root: {} };
          }
          const { light, dark } = filledLabelColors(palette, d?.primaryShade);
          /*
           * `--button-color` is what Mantine's own filled styles read, and it
           * has to be set inline to outrank Mantine's inline value. It cannot
           * carry the color directly, because this resolver has no way to know
           * the color scheme — so it points at a property index.css switches
           * per scheme. `light-dark()` would be the obvious tool and does not
           * work: inside a custom property it resolves to its light branch in
           * both schemes.
           */
          return {
            root: {
              '--btn-label-light': light,
              '--btn-label-dark': dark,
              '--button-color': 'var(--btn-label-active, #fff)',
            },
          };
        },
      },
    },
  } as MantineThemeOverride;
}

interface AppThemeContextValue {
  /** Current app theme */
  currentTheme: AppThemeDefinition;
  /** Current theme ID */
  themeId: AppThemeId;
  /** Set the app theme by ID */
  setTheme: (themeId: AppThemeId) => void;
  /** Get all available themes */
  availableThemes: AppThemeDefinition[];
  /** Get Mantine theme override for current theme */
  mantineThemeOverride: MantineThemeOverride;
  /** Current app style */
  currentStyle: AppStyleDefinition;
  /** Current style ID */
  styleId: AppStyleId;
  /** Set the app style by ID */
  setStyle: (styleId: AppStyleId) => void;
  /** Get all available styles */
  availableStyles: AppStyleDefinition[];
  /** Current dark mode tint */
  currentDarkModeTint: DarkModeTintDefinition;
  /** Current dark mode tint ID */
  darkModeTintId: DarkModeTintId;
  /** Set the dark mode tint by ID */
  setDarkModeTint: (tintId: DarkModeTintId) => void;
  /** Get all available dark mode tints */
  availableDarkModeTints: DarkModeTintDefinition[];
  /** Current dark mode base */
  currentDarkModeBase: DarkModeBaseDefinition;
  /** Current dark mode base ID */
  darkModeBaseId: DarkModeBaseId;
  /** Set the dark mode base by ID */
  setDarkModeBase: (baseId: DarkModeBaseId) => void;
  /** Get all available dark mode bases */
  availableDarkModeBases: DarkModeBaseDefinition[];
  /** Get current tint colors based on selected base */
  currentTintColors: DarkModeTintColors;
}

const AppThemeContext = createContext<AppThemeContextValue | undefined>(undefined);

interface AppThemeProviderProps {
  readonly children: ReactNode;
}

export function AppThemeProvider({ children }: AppThemeProviderProps): JSX.Element {
  const [themeId, setThemeId] = useState<AppThemeId>('default');
  const [styleId, setStyleId] = useState<AppStyleId>('default');
  const [darkModeTintId, setDarkModeTintId] = useState<DarkModeTintId>('medium');
  const [darkModeBaseId, setDarkModeBaseId] = useState<DarkModeBaseId>('blue'); // Default to blue-gray for subtle appearance

  // Load preferences from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem(STORAGE_KEY);
    if (savedTheme && APP_THEMES.some((t) => t.id === savedTheme)) {
      setThemeId(savedTheme as AppThemeId);
    }

    const savedStyle = localStorage.getItem(STYLE_STORAGE_KEY);
    if (savedStyle && APP_STYLES.some((s) => s.id === savedStyle)) {
      setStyleId(savedStyle as AppStyleId);
    }

    const savedTint = localStorage.getItem(DARK_MODE_TINT_STORAGE_KEY);
    if (savedTint && DARK_MODE_TINTS.some((t) => t.id === savedTint)) {
      setDarkModeTintId(savedTint as DarkModeTintId);
    }

    const savedBase = localStorage.getItem(DARK_MODE_BASE_STORAGE_KEY);
    if (savedBase && DARK_MODE_BASES.some((b) => b.id === savedBase)) {
      setDarkModeBaseId(savedBase as DarkModeBaseId);
    }
  }, []);

  const currentTheme = useMemo(() => {
    return APP_THEMES.find((t) => t.id === themeId) || DEFAULT_THEME;
  }, [themeId]);

  const currentStyle = useMemo(() => {
    return APP_STYLES.find((s) => s.id === styleId) || APP_STYLES[0];
  }, [styleId]);

  const currentDarkModeTint = useMemo(() => {
    return DARK_MODE_TINTS.find((t) => t.id === darkModeTintId) || DARK_MODE_TINTS[1]; // Default to 'medium'
  }, [darkModeTintId]);

  const currentDarkModeBase = useMemo(() => {
    return DARK_MODE_BASES.find((b) => b.id === darkModeBaseId) || DARK_MODE_BASES[1]; // Default to 'blue'
  }, [darkModeBaseId]);

  // Get the current tint colors based on the selected base
  const currentTintColors = useMemo(() => {
    return currentDarkModeTint.colors[darkModeBaseId];
  }, [currentDarkModeTint, darkModeBaseId]);

  const mantineThemeOverride = useMemo(() => {
    return generateMantineTheme(currentTheme);
  }, [currentTheme]);

  // Apply CSS custom properties for the theme
  useEffect(() => {
    const root = document.documentElement;
    // Main theme colors
    root.style.setProperty('--carepoint-app-primary', currentTheme.colors.primary[5]);
    // Companion contrast color, for CSS that fills with the primary directly.
    root.style.setProperty(
      '--carepoint-app-primary-contrast',
      contrastTextOn(currentTheme.colors.primary[5])
    );
    root.style.setProperty('--carepoint-app-secondary', currentTheme.colors.secondary[5]);
    root.style.setProperty('--carepoint-app-accent', currentTheme.colors.accent[5]);
    root.style.setProperty('--carepoint-app-neutral', currentTheme.colors.neutral[5]);

    // Background colors for flat style - using neutral palette
    root.style.setProperty('--carepoint-app-bg-lightest', currentTheme.colors.neutral[0]);
    root.style.setProperty('--carepoint-app-bg-light', currentTheme.colors.neutral[1]);
    root.style.setProperty('--carepoint-app-bg-medium', currentTheme.colors.neutral[2]);

    // Chart and graph colors - primary, secondary, accent shades
    root.style.setProperty('--carepoint-chart-primary', currentTheme.colors.primary[5]);
    root.style.setProperty('--carepoint-chart-primary-light', currentTheme.colors.primary[3]);
    root.style.setProperty('--carepoint-chart-secondary', currentTheme.colors.secondary[5]);
    root.style.setProperty('--carepoint-chart-secondary-light', currentTheme.colors.secondary[3]);
    root.style.setProperty('--carepoint-chart-accent', currentTheme.colors.accent[5]);
    root.style.setProperty('--carepoint-chart-accent-light', currentTheme.colors.accent[3]);

    // Success, warning, error colors for charts
    root.style.setProperty('--carepoint-chart-success', currentTheme.colors.success[5]);
    root.style.setProperty('--carepoint-chart-warning', currentTheme.colors.warning[5]);
    root.style.setProperty('--carepoint-chart-error', currentTheme.colors.error[5]);

    // Icon wrapper colors - using theme palette shades
    root.style.setProperty('--carepoint-icon-blue-bg', currentTheme.colors.primary[0]);
    root.style.setProperty('--carepoint-icon-blue-fg', currentTheme.colors.primary[5]);
    root.style.setProperty('--carepoint-icon-teal-bg', currentTheme.colors.secondary[0]);
    root.style.setProperty('--carepoint-icon-teal-fg', currentTheme.colors.secondary[5]);
    root.style.setProperty('--carepoint-icon-accent-bg', currentTheme.colors.accent[0]);
    root.style.setProperty('--carepoint-icon-accent-fg', currentTheme.colors.accent[5]);
    root.style.setProperty('--carepoint-icon-success-bg', currentTheme.colors.success[0]);
    root.style.setProperty('--carepoint-icon-success-fg', currentTheme.colors.success[5]);
    root.style.setProperty('--carepoint-icon-warning-bg', currentTheme.colors.warning[0]);
    root.style.setProperty('--carepoint-icon-warning-fg', currentTheme.colors.warning[5]);
    root.style.setProperty('--carepoint-icon-error-bg', currentTheme.colors.error[0]);
    root.style.setProperty('--carepoint-icon-error-fg', currentTheme.colors.error[5]);
  }, [currentTheme]);

  // Load a theme's web fonts on demand, and remove them when it is deselected.
  // Themes on system fonts declare none, so they load nothing.
  useEffect(() => {
    const ATTR = 'data-carepoint-theme-font';
    document.querySelectorAll(`link[${ATTR}]`).forEach((el) => el.remove());

    const fonts = currentTheme.design?.webFonts;
    if (!fonts?.length) {
      return;
    }
    for (const href of fonts) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.setAttribute(ATTR, currentTheme.id);
      document.head.appendChild(link);
    }
  }, [currentTheme]);

  // Apply the theme's own body class, so CSS modules can opt into
  // theme-specific treatment without fighting a global selector.
  useEffect(() => {
    const body = document.body;
    const applied: string[] = [];
    for (const theme of APP_THEMES) {
      if (theme.design?.bodyClass) {
        applied.push(theme.design.bodyClass);
      }
    }
    body.classList.remove(...applied);
    if (currentTheme.design?.bodyClass) {
      body.classList.add(currentTheme.design.bodyClass);
    }
  }, [currentTheme]);

  // Apply style class to body for CSS-based flat styling
  useEffect(() => {
    const body = document.body;
    // Remove all style classes first
    body.classList.remove('app-style-default', 'app-style-flat');
    // Add the current style class
    body.classList.add(`app-style-${styleId}`);
  }, [styleId]);

  // Apply dark mode tint CSS variables (uses currentTintColors which combines tint + base)
  useEffect(() => {
    const root = document.documentElement;

    // Set CSS custom properties for dark mode tint
    root.style.setProperty('--dark-tint-9', currentTintColors.dark9);
    root.style.setProperty('--dark-tint-8', currentTintColors.dark8);
    root.style.setProperty('--dark-tint-7', currentTintColors.dark7);
    root.style.setProperty('--dark-tint-6', currentTintColors.dark6);
    root.style.setProperty('--dark-tint-5', currentTintColors.dark5);
    root.style.setProperty('--dark-tint-4', currentTintColors.dark4);
    root.style.setProperty('--dark-tint-gray-0', currentTintColors.gray0);
    root.style.setProperty('--dark-tint-gray-3', currentTintColors.gray3);
    root.style.setProperty('--dark-tint-gray-5', currentTintColors.gray5);

    // Apply tint and base classes to body for CSS-based dark mode styling
    const body = document.body;
    body.classList.remove('dark-tint-enhanced', 'dark-tint-medium', 'dark-tint-gentle');
    body.classList.add(`dark-tint-${currentDarkModeTint.id}`);
    body.classList.remove('dark-base-neutral', 'dark-base-blue');
    body.classList.add(`dark-base-${darkModeBaseId}`);
  }, [currentTintColors, currentDarkModeTint.id, darkModeBaseId]);

  const setTheme = useCallback((newThemeId: AppThemeId): void => {
    if (APP_THEMES.some((t) => t.id === newThemeId)) {
      setThemeId(newThemeId);
      localStorage.setItem(STORAGE_KEY, newThemeId);
    }
  }, []);

  const setStyle = useCallback((newStyleId: AppStyleId): void => {
    if (APP_STYLES.some((s) => s.id === newStyleId)) {
      setStyleId(newStyleId);
      localStorage.setItem(STYLE_STORAGE_KEY, newStyleId);
    }
  }, []);

  const setDarkModeTint = useCallback((newTintId: DarkModeTintId): void => {
    if (DARK_MODE_TINTS.some((t) => t.id === newTintId)) {
      setDarkModeTintId(newTintId);
      localStorage.setItem(DARK_MODE_TINT_STORAGE_KEY, newTintId);
    }
  }, []);

  const setDarkModeBase = useCallback((newBaseId: DarkModeBaseId): void => {
    if (DARK_MODE_BASES.some((b) => b.id === newBaseId)) {
      setDarkModeBaseId(newBaseId);
      localStorage.setItem(DARK_MODE_BASE_STORAGE_KEY, newBaseId);
    }
  }, []);

  const value = useMemo(
    (): AppThemeContextValue => ({
      currentTheme,
      themeId,
      setTheme,
      availableThemes: APP_THEMES,
      mantineThemeOverride,
      currentStyle,
      styleId,
      setStyle,
      availableStyles: APP_STYLES,
      currentDarkModeTint,
      darkModeTintId,
      setDarkModeTint,
      availableDarkModeTints: DARK_MODE_TINTS,
      currentDarkModeBase,
      darkModeBaseId,
      setDarkModeBase,
      availableDarkModeBases: DARK_MODE_BASES,
      currentTintColors,
    }),
    [currentTheme, themeId, setTheme, mantineThemeOverride, currentStyle, styleId, setStyle, currentDarkModeTint, darkModeTintId, setDarkModeTint, currentDarkModeBase, darkModeBaseId, setDarkModeBase, currentTintColors]
  );

  return <AppThemeContext.Provider value={value}>{children}</AppThemeContext.Provider>;
}

export function useAppTheme(): AppThemeContextValue {
  const context = useContext(AppThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within an AppThemeProvider');
  }
  return context;
}
